"use client";

import { useEffect, useState } from "react";
import { Box, Spinner, Heading, Flex, Tooltip, useTheme } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

export default function Chart7({ supplier, retailer }) {
  const theme = useTheme();
  const [chartDiv, setChartDiv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [height, setHeight] = useState("300px");
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    let root;
    if (!chartDiv) return;
    setLoading(true);

    axios
      .post(`${process.env.REACT_APP_API_URL}/rep_sales/chart7`, {
        supplier,
        retailer,
        userid: localStorage.getItem("userid"),
      })
      .then((res) => {

        const raw = Array.isArray(res.data?.data) ? res.data.data : [];
        // console.log("chart 7raw:");
        // console.log(raw);
        const data = raw
          .map((r) => {
            const value = Number(r?.value);
            if (isNaN(value) || value <= 0) return null;
            const pct = Math.round(value * 1000) / 10;
            return {
              region: r.region || "Unknown",
              value: pct,
              percentLabel: `${pct.toFixed(1)} %`,
              color: r.color?.fill || "#12a3c4",
            };
          })
          .filter(Boolean);

        if (!data.length) {
          setNoData(true);
          return;
        }
        setNoData(false);

        setHeight(`${Math.max(data.length * 50, 300)}px`);

        root = am5.Root.new(chartDiv);
        root.setThemes([am5themes_Animated.new(root)]);

        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            paddingLeft: 15,
            paddingRight: 25,
            paddingBottom: 30,
          })
        );

        const yAxis = chart.yAxes.push(
          am5xy.CategoryAxis.new(root, {
            categoryField: "region",
            renderer: am5xy.AxisRendererY.new(root, {
              inversed: true,
              cellStartLocation: 0.1,
              cellEndLocation: 0.9,
              minGridDistance: 20,
            }),
          })
        );

        yAxis.data.setAll(data);
        yAxis.get("renderer").labels.template.setAll({
          fontSize: 11,
          maxWidth: 140,
          oversizedBehavior: "wrap",
          textAlign: "end",
          fill: am5.color("#9ca3af"),
        });
        yAxis.get("renderer").grid.template.setAll({ visible: false });

        const xAxis = chart.xAxes.push(
          am5xy.ValueAxis.new(root, {
            min: 0,
            max: 100,
            strictMinMax: true,
            renderer: am5xy.AxisRendererX.new(root, { strokeOpacity: 0 }),
          })
        );
        xAxis.set("numberFormat", "#,###.#'%'");
        xAxis.get("renderer").grid.template.setAll({
          strokeOpacity: 0.15,
          strokeDasharray: [3, 3],
          stroke: am5.color("#e5e7eb"),
        });
        xAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color("#9ca3af") });

        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: "value",
            categoryYField: "region",
            sequencedInterpolation: true,
          })
        );

        const tooltip = am5.Tooltip.new(root, {
          getFillFromSprite: false,
          labelText: "{categoryY}: {percentLabel}",
        });
        tooltip.get("background").setAll({
          fill: am5.color("#ffffff"),
          fillOpacity: 0.95,
          stroke: am5.color("#e5e7eb"),
          strokeWidth: 1,
          shadowColor: am5.color("#000000"),
          shadowBlur: 8,
          shadowOffsetX: 0,
          shadowOffsetY: 2,
          shadowOpacity: 0.08,
          cornerRadius: 6,
        });
        tooltip.label.setAll({
          fill: am5.color("#374151"),
          fontSize: 12,
        });

        series.data.setAll(data);
        series.columns.template.setAll({
          height: am5.percent(100),
          strokeOpacity: 0,
          fillOpacity: 0.8,
          cornerRadiusTR: 4,
          cornerRadiusBR: 4,
          tooltip: tooltip,
          tooltipText: "{categoryY}: {percentLabel}",
          fill: am5.color("#58b6c0"),
        });

        series.bullets.push(() =>
          am5.Bullet.new(root, {
            locationX: 1,
            sprite: am5.Label.new(root, {
              text: "{percentLabel}",
              populateText: true,
              centerY: am5.p50,
              dx: 10,
              fontSize: 10,
              fill: am5.color("#6b7280"),
            }),
          })
        );

        chart.appear(1000, 100);
      })
      .catch((err) => {
        console.error("Chart7 fetch", err);
      })
      .finally(() => setLoading(false));

    return () => root && root.dispose();
  }, [chartDiv, supplier, retailer]);

  return (
    <Box height={height} display="flex" flexDirection="column" position="relative">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">Sell-Through % by Region</Heading>
        <Tooltip label="Percentage of this month’s opening stock that has been sold so far." placement="left" hasArrow>
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      <Box flex="1" position="relative">
        {noData && (
          <Flex align="center" justify="center" position="absolute" top="0" left="0" width="100%" height="100%" bg="whiteAlpha.800" zIndex="1">
            <Heading size="sm" color="gray.600">
              No data available
            </Heading>
          </Flex>
        )}
        <div ref={setChartDiv} style={{ width: "100%", height: "100%", overflow: "hidden" }} />
        {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
      </Box>
    </Box>
  );
}
